import { formatCPFOrCNPJ } from 'src/app/helpers/utils/cpf-cnpj.util';
import { Beneficiaries } from 'src/app/shared/interfaces/beneficiaries';
import { CarbonOperation } from 'src/app/shared/interfaces/carbon-operation';
import { OperationType } from 'src/app/shared/interfaces/operation-type.enum';

import { WithdrawalBeneficiaryRow } from '../modal-beneficiaries-table/modal-beneficiaries-table.component';

export interface RetirementEntityView {
  withdrawalReason: string;
  retirementOnBehalfOf: string;
  documentLabel: string;
  documentNumber: string;
  quantity: string;
  companyName: string;
  detailsDescription: string;
}

export function isB3RetirementOperation(operation: CarbonOperation): boolean {
  return (
    operation.rawTypeCode === OperationType.WITHDRAW &&
    operation.retirementCode === 'A'
  );
}

export function isAcxRetirementOperation(operation: CarbonOperation): boolean {
  return operation.rawTypeCode === OperationType.RETIREMENT;
}

export function isRetirementOperation(operation: CarbonOperation): boolean {
  return isB3RetirementOperation(operation) || isAcxRetirementOperation(operation);
}

export function normalizeRetirementBeneficiary(
  beneficiary: WithdrawalBeneficiaryRow | Beneficiaries,
): Beneficiaries {
  const row = beneficiary as WithdrawalBeneficiaryRow & Beneficiaries;

  return {
    beneficiaryName: row.beneficiaryName ?? row.withdrawalName,
    documentNumber: row.documentNumber ?? row.cpfCnpj,
    beneficiaryDescription: row.beneficiaryDescription ?? row.description,
    foreignerIndicator: row.foreignerIndicator ?? row.foreigner,
    withdrawalAmount: normalizeWithdrawalAmount(row.withdrawalAmount ?? row.amount),
  };
}

export function mapRetirementBeneficiaryToView(
  beneficiary: WithdrawalBeneficiaryRow | Beneficiaries,
): RetirementEntityView {
  const normalized = normalizeRetirementBeneficiary(beneficiary);
  const isForeignEntity = !!normalized.foreignerIndicator;

  return {
    withdrawalReason: 'Aposentadoria',
    retirementOnBehalfOf: isForeignEntity ? 'Entidade estrangeira' : 'Entidade nacional',
    documentLabel: isForeignEntity ? 'TAX ID' : 'CPF/CNPJ',
    documentNumber: formatRetirementDocument(normalized.documentNumber, isForeignEntity),
    quantity: displayRetirementQuantity(normalized.withdrawalAmount),
    companyName: displayRetirementValue(normalized.beneficiaryName),
    detailsDescription: displayRetirementValue(normalized.beneficiaryDescription),
  };
}

export function mapRetirementBeneficiariesToViews(
  beneficiaries: Array<WithdrawalBeneficiaryRow | Beneficiaries>,
): RetirementEntityView[] {
  return beneficiaries.map(mapRetirementBeneficiaryToView);
}

function formatRetirementDocument(value?: string | null, isForeignEntity = false): string {
  if (!value?.trim()) {
    return '-';
  }

  if (isForeignEntity) {
    return value.trim();
  }

  return formatCPFOrCNPJ(value) ?? value;
}

function displayRetirementQuantity(value?: number | string | null): string {
  if (value === null || value === undefined || value === '') {
    return '-';
  }

  return `${value}`;
}

function displayRetirementValue(value?: string | null): string {
  const trimmed = value?.trim();
  return trimmed ? trimmed : '-';
}

function normalizeWithdrawalAmount(value?: string | number | null): number | undefined {
  if (value === null || value === undefined || value === '') {
    return undefined;
  }

  if (typeof value === 'number') {
    return value;
  }

  const parsed = Number(value);
  return Number.isNaN(parsed) ? undefined : parsed;
}
